/**
 * Avisos discretos de vigencia por módulo (solo presentación; no altera permisos).
 */
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import {
  daysLeftUntilValidTo,
  resolveLicenseExpirationDate,
} from "@/lib/licenseRenewal";

/** Días antes del vencimiento en los que se muestra aviso bajo el módulo. */
export const MODULE_EXPIRY_WARNING_DAYS = 15;

function pickValidToFromMap(map, moduleId) {
  if (!map || typeof map !== "object") return null;
  const raw = map[moduleId];
  if (!raw) return null;
  if (typeof raw === "string") return String(raw).slice(0, 10);
  if (typeof raw === "object" && raw.valid_to) return String(raw.valid_to).slice(0, 10);
  return null;
}

/**
 * Vigencia efectiva de un módulo: específica si existe en payload, si no la general.
 * @param {object} [systemInfo]
 * @param {string} moduleId
 */
export function resolveModuleValidTo(systemInfo = {}, moduleId) {
  const payload = systemInfo?.license?.payload || {};
  return (
    pickValidToFromMap(payload.module_valid_to, moduleId)
    || pickValidToFromMap(payload.modules_valid_to, moduleId)
    || pickValidToFromMap(payload.entitlements_validity, moduleId)
    || resolveLicenseExpirationDate(systemInfo)
  );
}

/**
 * Texto de aviso bajo módulo activo, o null si faltan más de windowDays días.
 * @param {string|null|undefined} validToIso YYYY-MM-DD
 * @param {number} [windowDays]
 * @returns {{ kind: 'expiring'|'expired', text: string, daysLeft: number }|null}
 */
export function buildModuleExpiryHint(validToIso, windowDays = MODULE_EXPIRY_WARNING_DAYS) {
  if (!validToIso) return null;
  const daysLeft = daysLeftUntilValidTo(validToIso);
  if (typeof daysLeft !== "number") return null;
  if (daysLeft > windowDays) return null;

  const dateLabel = formatLicenseDateForDisplay(validToIso);
  if (daysLeft < 0) {
    return {
      kind: "expired",
      text: `Vencido el ${dateLabel}`,
      daysLeft,
    };
  }
  if (daysLeft === 0) {
    return {
      kind: "expiring",
      text: `Vence hoy (${dateLabel})`,
      daysLeft,
    };
  }
  return {
    kind: "expiring",
    text: `Por vencer en ${daysLeft} ${daysLeft === 1 ? "día" : "días"}`,
    daysLeft,
  };
}

/**
 * @param {object} [systemInfo]
 * @param {string} moduleId
 * @param {number} [windowDays]
 */
export function moduleExpiryHintForModule(systemInfo, moduleId, windowDays = MODULE_EXPIRY_WARNING_DAYS) {
  const validTo = resolveModuleValidTo(systemInfo, moduleId);
  return buildModuleExpiryHint(validTo, windowDays);
}
